import { useParams, Link } from "wouter";
import { useState, useEffect, useMemo } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { formatCurrency, safeDateFormat } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import {
  useGetStatement,
  useGetNominalTemplates,
  useGetNominalAssignmentsForStatement,
  useAnalyzeReceiptBreakdown,
  useSaveNominalAssignment,
} from "@workspace/api-client-react";
import {
  ArrowLeft, Hash, Sparkles, Check, Save, ChevronDown, ChevronRight,
  User, Loader2, FileText, AlertCircle,
} from "lucide-react";

interface BreakdownItem {
  category: string;
  items: string[];
  total: number;
  nominalCode: string;
}

export default function NominalAssignmentPage() {
  const params = useParams();
  const statementId = parseInt(params.id || "0", 10);
  const { data: statementData, isLoading: stmtLoading } = useGetStatement(statementId);
  const { data: templates } = useGetNominalTemplates();
  const { data: existingAssignments, refetch: refetchAssignments } = useGetNominalAssignmentsForStatement(statementId);
  const analyzeMutation = useAnalyzeReceiptBreakdown();
  const saveMutation = useSaveNominalAssignment();
  const { toast } = useToast();

  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [nominalInputs, setNominalInputs] = useState<Record<number, string>>({});
  const [breakdowns, setBreakdowns] = useState<Record<number, BreakdownItem[]>>({});
  const [analyzingEntry, setAnalyzingEntry] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const assignmentMap = useMemo(() => {
    const map = new Map<number, any[]>();
    if (existingAssignments) {
      for (const a of existingAssignments as any[]) {
        const arr = map.get(a.statementEntryId) || [];
        arr.push(a);
        map.set(a.statementEntryId, arr);
      }
    }
    return map;
  }, [existingAssignments]);

  useEffect(() => {
    if (existingAssignments) {
      const inputs: Record<number, string> = {};
      const bds: Record<number, BreakdownItem[]> = {};
      for (const a of existingAssignments as any[]) {
        if (a.breakdownJson) {
          const arr = bds[a.statementEntryId] || [];
          arr.push(a.breakdownJson as BreakdownItem);
          bds[a.statementEntryId] = arr;
        } else if (a.nominalCode) {
          inputs[a.statementEntryId] = a.nominalCode;
        }
      }
      setNominalInputs((prev) => ({ ...prev, ...inputs }));
      setBreakdowns((prev) => ({ ...prev, ...bds }));
    }
  }, [existingAssignments]);

  if (stmtLoading) {
    return <Layout><div className="p-8 text-center">Loading...</div></Layout>;
  }

  if (!statementData) {
    return <Layout><div className="p-8 text-center text-red-500">Statement not found.</div></Layout>;
  }

  const { statement, entries } = statementData;

  const filteredEntries = entries.filter((e: any) =>
    e.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
    e.cardholderName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const matchedEntries = filteredEntries.filter((e: any) => e.matched);

  const groupMap = new Map<string, any[]>();
  for (const entry of matchedEntries) {
    const name = entry.cardholderName || "Unknown";
    if (!groupMap.has(name)) groupMap.set(name, []);
    groupMap.get(name)!.push(entry);
  }
  const groups = Array.from(groupMap.entries())
    .map(([name, entries]) => ({ name, entries }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const toggleGroup = (name: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const findMatchingTemplate = (entry: any) => {
    if (!templates) return null;
    const desc = entry.description?.toLowerCase() || "";
    const receipt = entry.matchedReceipt || entry.matchedReceipts?.[0];
    const vendor = receipt?.vendor?.toLowerCase() || "";
    return (templates as any[]).find((t: any) => {
      const pattern = t.vendorPattern.toLowerCase();
      return desc.includes(pattern) || vendor.includes(pattern);
    });
  };

  const handleAnalyze = async (entry: any) => {
    const receipt = entry.matchedReceipt || entry.matchedReceipts?.[0];
    if (!receipt) {
      toast({ title: "No receipt", description: "This entry has no matched receipt to analyze", variant: "destructive" });
      return;
    }

    const template = findMatchingTemplate(entry);
    setAnalyzingEntry(entry.id);

    try {
      const params: any = { data: { receiptId: receipt.id } };
      if (template) {
        params.data.templateId = template.id;
      } else {
        toast({ title: "No template found", description: "No matching nominal template. Please create one for this vendor first.", variant: "destructive" });
        setAnalyzingEntry(null);
        return;
      }

      const result = await analyzeMutation.mutateAsync(params);
      if (result.breakdown) {
        setBreakdowns((prev) => ({ ...prev, [entry.id]: result.breakdown }));
        toast({ title: "Analysis complete", description: `Found ${result.breakdown.length} categories` });
      }
    } catch (err: any) {
      toast({ title: "Analysis failed", description: err.message || "Unknown error", variant: "destructive" });
    } finally {
      setAnalyzingEntry(null);
    }
  };

  const handleSaveSimple = async (entryId: number) => {
    const code = nominalInputs[entryId];
    if (!code) return;

    try {
      await saveMutation.mutateAsync({
        data: { statementEntryId: entryId, nominalCode: code },
      });
      toast({ title: "Nominal code saved" });
      refetchAssignments();
    } catch {
      toast({ title: "Error", description: "Failed to save", variant: "destructive" });
    }
  };

  const handleSaveBreakdown = async (entryId: number) => {
    const bd = breakdowns[entryId];
    if (!bd?.length) return;

    try {
      await saveMutation.mutateAsync({
        data: { statementEntryId: entryId, breakdownJson: bd },
      });
      toast({ title: "Breakdown saved" });
      refetchAssignments();
    } catch {
      toast({ title: "Error", description: "Failed to save breakdown", variant: "destructive" });
    }
  };

  const hasAssignment = (entryId: number) => {
    return assignmentMap.has(entryId);
  };

  const getAssignmentSummary = (entryId: number) => {
    const assignments = assignmentMap.get(entryId);
    if (!assignments?.length) return null;
    if (assignments.length === 1 && assignments[0].nominalCode && !assignments[0].breakdownJson) {
      return { type: "simple" as const, code: assignments[0].nominalCode };
    }
    return { type: "breakdown" as const, count: assignments.length };
  };

  const totalAssigned = matchedEntries.filter((e: any) => hasAssignment(e.id)).length;

  return (
    <Layout>
      <div className="mb-6">
        <Link href={`/statements/${statementId}`} className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-primary mb-4 transition-colors">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Statement
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-slate-900">Nominal Code Assignment</h1>
            <p className="text-slate-500 mt-1">
              {statement.originalFilename} — Assign nominal codes to matched entries
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Matched Entries</p>
            <p className="text-2xl font-bold text-slate-900">{matchedEntries.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Codes Assigned</p>
            <p className="text-2xl font-bold text-green-600">{totalAssigned}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-5">
            <p className="text-sm font-medium text-slate-500">Remaining</p>
            <p className="text-2xl font-bold text-amber-600">{matchedEntries.length - totalAssigned}</p>
          </CardContent>
        </Card>
      </div>

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search entries..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full max-w-sm px-4 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
        />
      </div>

      <div className="space-y-2">
        {groups.map((group) => {
          const isCollapsed = collapsedGroups.has(group.name);
          const groupAssigned = group.entries.filter((e: any) => hasAssignment(e.id)).length;

          return (
            <div key={group.name} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div
                className="px-6 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors flex items-center justify-between"
                onClick={() => toggleGroup(group.name)}
              >
                <div className="flex items-center gap-3">
                  {isCollapsed ? <ChevronRight className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  <User className="w-4 h-4 text-slate-500" />
                  <span className="font-semibold text-slate-800">{group.name}</span>
                  <span className="text-xs text-slate-500">{group.entries.length} entries</span>
                </div>
                <span className={`text-sm font-medium ${groupAssigned === group.entries.length ? "text-green-600" : "text-amber-600"}`}>
                  {groupAssigned}/{group.entries.length} assigned
                </span>
              </div>

              {!isCollapsed && (
                <div className="divide-y divide-slate-100">
                  {group.entries.map((entry: any) => {
                    const template = findMatchingTemplate(entry);
                    const assignment = getAssignmentSummary(entry.id);
                    const bd = breakdowns[entry.id];
                    const isAnalyzing = analyzingEntry === entry.id;

                    return (
                      <div key={entry.id} className="px-6 py-4">
                        <div className="flex items-start gap-4">
                          <div className="shrink-0 mt-1">
                            {assignment ? (
                              <div className="w-8 h-8 rounded-full bg-green-100 text-green-600 flex items-center justify-center">
                                <Check className="w-4 h-4" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-slate-100 text-slate-400 flex items-center justify-center">
                                <Hash className="w-4 h-4" />
                              </div>
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-3 mb-1">
                              <p className="font-medium text-slate-900 truncate">{entry.description}</p>
                              <span className="text-sm font-semibold text-slate-700">{formatCurrency(entry.amount)}</span>
                              <span className="text-xs text-slate-400">{safeDateFormat(entry.transactionDate)}</span>
                            </div>

                            {entry.matchedReceipt && (
                              <p className="text-xs text-slate-500 mb-2">
                                <FileText className="w-3 h-3 inline mr-1" />
                                Receipt: {entry.matchedReceipt.vendor || entry.matchedReceipt.originalFilename}
                              </p>
                            )}

                            {assignment && assignment.type === "simple" && !bd?.length && (
                              <div className="mb-2">
                                <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full">
                                  <Check className="w-3 h-3" /> Nominal: {assignment.code}
                                </span>
                              </div>
                            )}

                            {assignment && assignment.type === "breakdown" && !bd?.length && (
                              <div className="mb-2">
                                <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full">
                                  <Check className="w-3 h-3" /> {assignment.count} categories assigned
                                </span>
                              </div>
                            )}

                            {bd && bd.length > 0 && (
                              <div className="mt-3 bg-slate-50 rounded-lg p-4 space-y-3">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-sm font-semibold text-slate-700">AI Breakdown</h4>
                                  <Button size="sm" onClick={() => handleSaveBreakdown(entry.id)} disabled={saveMutation.isPending}>
                                    <Save className="w-3 h-3 mr-1" /> Save Breakdown
                                  </Button>
                                </div>
                                {bd.map((item, i) => (
                                  <div key={i} className="bg-white rounded-lg p-3 border border-slate-200">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-medium text-sm text-slate-800">{item.category}</span>
                                      <div className="flex items-center gap-2">
                                        <span className="text-sm font-semibold">{formatCurrency(item.total)}</span>
                                        <span className="text-xs font-mono bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{item.nominalCode}</span>
                                      </div>
                                    </div>
                                    <p className="text-xs text-slate-500">
                                      {item.items.join(", ")}
                                    </p>
                                  </div>
                                ))}
                                <div className="text-right">
                                  <span className="text-xs text-slate-500">
                                    Total: {formatCurrency(bd.reduce((sum, item) => sum + item.total, 0))}
                                  </span>
                                </div>
                              </div>
                            )}

                            <div className="flex items-center gap-2 mt-2 flex-wrap">
                              {template && (entry.matchedReceipt || entry.matchedReceipts?.length > 0) && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleAnalyze(entry)}
                                  disabled={isAnalyzing}
                                >
                                  {isAnalyzing ? (
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                  ) : (
                                    <Sparkles className="w-3 h-3 mr-1" />
                                  )}
                                  {isAnalyzing ? "Analyzing..." : `AI Breakdown (${template.vendorPattern})`}
                                </Button>
                              )}

                              {!bd?.length && (
                                <div className="flex items-center gap-2">
                                  <input
                                    type="text"
                                    placeholder="Nominal code"
                                    value={nominalInputs[entry.id] || ""}
                                    onChange={(e) =>
                                      setNominalInputs((prev) => ({ ...prev, [entry.id]: e.target.value }))
                                    }
                                    className="w-32 px-2 py-1 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/50"
                                  />
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleSaveSimple(entry.id)}
                                    disabled={!nominalInputs[entry.id] || saveMutation.isPending}
                                  >
                                    <Save className="w-3 h-3 mr-1" /> Save
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {matchedEntries.length === 0 && (
          <Card>
            <CardContent className="p-12 text-center">
              <Hash className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-700">No matched entries</h3>
              <p className="text-slate-500">Run auto-matching first to match receipts to statement entries</p>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}
